import type { WizardState } from "../wizardTypes";
import { SUPPORT_EMAIL, SUPPORT_PHONE } from "../wizardTypes";
import { fmtMoney } from "../data/planMatch";
import styles from "../css/SignupWizard.module.css";

type Props = {
  state: WizardState;
};

export function Confirmation({ state }: Props) {
  const { plan, payment, account } = state;
  if (!plan || !payment || !account) return null;
  const has = (key: string) => plan.lines.some((l) => l.productKey === key);
  const hasScrub = has("dncscrub");
  const hasRemediation = has("trustcall");
  const hasComplianceGuide = has("complianceguide");

  return (
    <div className={styles.confirmCenter}>
      <div className={styles.checkmark}>{"\u2713"}</div>
      <h2>You're all set!</h2>
      {account.isTestSignup && (
        <div className={styles.testBadge} role="status">
          TEST SIGNUP &mdash; NMI sandbox, no real card charge, invoice voided.
          Retire this account with RetireSelfServeTestAccount.sql when you are done.
        </div>
      )}

      {/* Receipt */}
      <div className={styles.receiptBlock}>
        <h3>Receipt</h3>
        {plan.lines.map((l) => (
          <div key={l.productKey} className={styles.summaryLine}>
            <span>{l.name}</span>
            <span>
              {fmtMoney(l.price)}/{l.period}
            </span>
          </div>
        ))}
        <hr />
        <div className={styles.summaryLine}>
          <span>{account.isTestSignup ? "Sandbox test amount" : "Charged today"}</span>
          <strong>{fmtMoney(payment.chargedAmount)}</strong>
        </div>
        <div className={styles.summaryLine}>
          <span>Next charge</span>
          <span>{account.isTestSignup ? "None — test account" : payment.nextChargeDate}</span>
        </div>
        <div className={styles.summaryLine}>
          <span>Card on file</span>
          <span>&bull;&bull;&bull;&bull; {payment.lastFour}</span>
        </div>
      </div>

      {/* Credentials */}
      <div className={styles.receiptBlock}>
        <h3>Your Login</h3>
        <div className={styles.summaryLine}>
          <span>Account ID</span>
          <span className={styles.credentialsMono}>{payment.accountId}</span>
        </div>
        <div className={styles.summaryLine}>
          <span>Username</span>
          <span className={styles.credentialsMono}>{account.username}</span>
        </div>
      </div>

      <a
        href={account.loginUrl.startsWith("http") || account.loginUrl.startsWith("/") ? account.loginUrl : "#"}
        className={styles.btnPrimary}
        target="_top" // the wizard runs inside a <dialog><iframe> on dnc.com; leave the frame to log in
        style={{
          textDecoration: "none",
          display: "block",
          textAlign: "center",
        }}
      >
        Log into DNCScrub
      </a>

      {/* Same promises as the welcome email, so the screen and the inbox agree. */}
      <div className={styles.confirmNext}>
        <h3>What happens next</h3>
        {account.isTestSignup ? (
          <p>Test signup emails were routed to internal test recipients. Use this account to
            verify the products below, then retire it when testing is complete.</p>
        ) : (
          <p>
            We&rsquo;ve emailed your Account ID and username to{" "}
            <strong>{state.answers.email}</strong>. A member of the Sonera team will
            reach out <strong>within 1 business day</strong> to finish setting up
            your account. Nothing is required from you until then.
          </p>
        )}
        <h3>Want to start now?</h3>
        <ul>
          {hasScrub && (
            <li>Log in and upload your first list &mdash; CSV or Excel, results in minutes.</li>
          )}
          {hasScrub && (
            <li>
              Calling from a dialer or CRM? Create your API key in the app, then follow the{" "}
              <a href="https://docs.dncscrub.com/api-reference/authentication" target="_blank" rel="noopener noreferrer">
                API guide
              </a>.
            </li>
          )}
          {hasRemediation && (
            <li>Add the caller ID numbers you dial from under TrustCall so the first carrier scan can run.</li>
          )}
          {hasComplianceGuide && (
            <li>Open the Compliance Guide and check the rules for the states you call most.</li>
          )}
          <li>
            Questions any time:{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or {SUPPORT_PHONE}.
          </li>
        </ul>
      </div>
    </div>
  );
}
